import type { HoverContent } from "./HoverCard";
import { clamp, percent } from "./scale";

export interface Bar {
  /** What the bar measures. */
  value: number;
  /** CSS colour, normally a `var(--…)` from `tokens.css`. */
  color: string;
  /** Native tooltip — one line, free and accessible. Still the right answer for the small panels
   *  (difficulty, the hourly breakdown) where a bar has exactly one thing to say. */
  label?: string;
  /** The designed hover card, for a bar with several figures behind it. Takes precedence over
   *  `label`: two tooltips on one mark is a bug, and the browser's would sit under the card. */
  hover?: HoverContent;
  /**
   * A second value on the **same scale**, drawn as an outline behind the bar — 日本-340's modelled
   * forecast beside the scheduled one.
   *
   * Deliberately not `overlay`, which is a per-bar rule on a *different* domain (the hourly panel's
   * pass rate). This is the same quantity as `value`, measured a different way, so it has to share
   * the scale or the comparison means nothing.
   *
   * **Outlined rather than filled, and behind rather than in front.** The modelled value is usually
   * the taller of the two, so a filled bar would bury the scheduled one it exists to be compared
   * against; an outline shows both whichever way round they fall, and reads as the modelled
   * quantity rather than a second measurement.
   */
  ghost?: { value: number; color: string };
  /**
   * A third value on the **same scale**, drawn as a rule across the bar at its level — 日本-346's
   * keep-mining forecast, above the scheduled fill and the modelled outline.
   *
   * **A rule, because the column is ~9px wide and three boxes do not read in it.** `value` is a
   * fill, `ghost` is an outline, and a third outline — the alternative that was drawn and rejected
   * — puts three borders inside one bar and collapses into a smear across 60 of them. A rule is
   * *shape*-distinct from both, so the three stay separable at that width whichever way round they
   * fall.
   *
   * **Not `overlay`.** That prop is a per-bar value on a DIFFERENT domain, mapped through its own
   * `domain` pair — the hourly panel's pass rate. This is the same quantity as `value` and must
   * share the scale, or the comparison the panel exists to make means nothing.
   *
   * Named in `ghost`'s register — what the mark looks like, not what the data is — so the chart
   * component keeps knowing nothing about forecasts.
   */
  crest?: { value: number; color: string };
}

/**
 * A row of vertical bars sharing one scale.
 *
 * Five panels use this — the unified load axis, difficulty, stability over time, the hourly
 * breakdown and coverage-over-time — and they differ only in their bars and their height. Sharing
 * one component is what stops bar gaps and rounding drifting between them.
 *
 * **`scaleMax` is a prop, not derived, and that is load-bearing for the load axis.** Past and future
 * bars are two calls to this component sitting side by side; if each computed its own maximum the
 * two halves would be normalised independently and the comparison the whole panel exists to make
 * would be silently destroyed while still looking like a chart. Callers that genuinely want an
 * auto-fit pass `niceMax` of their own peak.
 */
export function Bars({
  bars,
  scaleMax,
  height,
  gap = 1,
  minBarHeight = 2,
  className = "",
  fill = false,
  overlay,
  onEnter,
  onLeave,
}: {
  bars: Bar[];
  scaleMax?: number;
  height: number;
  gap?: number;
  /** A non-zero value never renders as nothing — a day with three reviews must not look like a day
   *  with none, which is the one distinction the heatmap beside it is built to make. */
  minBarHeight?: number;
  className?: string;
  /**
   * Grow to fill the space its panel has, instead of standing at `height`.
   *
   * **For a band, and only a band.** At 1920 a card is stretched to its row's height (`layout.tsx`)
   * and a fixed-height chart then sits in the top third of it with the rest empty — which is what a
   * stretched card looks like when only its box was told to stretch (Alex, 8 Aug 2026). `height`
   * becomes the floor rather than the size.
   *
   * `flex: 1 1 auto` rather than `1 1 0%` so this is safe in a panel whose height is its content:
   * with no free space to distribute it simply stands at `min-height`. It must NOT be set on a
   * `Bars` that is a flex item in a ROW — the load axis's two halves — where flex-grow would take
   * its width instead.
   */
  fill?: boolean;
  /**
   * A second series drawn as a rule across each bar — the design's pass-rate marker on the hourly
   * panel (`8b`). A **rule rather than a line**, because it is a per-bar value and joining them
   * would claim a continuity between 23:00 and 00:00 that does not exist.
   *
   * `domain` is chosen, never fitted, for `Line`'s reason: an auto-fitted band makes a flat day
   * look like a crisis. `null` draws nothing — an hour with no reviews has no rate.
   */
  overlay?: { values: (number | null)[]; domain: [number, number]; color: string };
  /** From `useChartHover`, for bars carrying `hover`. */
  onEnter?: (event: { currentTarget: Element }, content: HoverContent) => void;
  onLeave?: () => void;
}) {
  // **The peak, not a rounded "nice" number.** These rows have no y-axis and no ticks, so a
  // nice maximum buys nothing and costs height: at `niceMax`, a peak of 1,020 scales against 2,000
  // and the tallest bar in the panel reaches half way (Alex, 8 Aug 2026 — the hourly panel). The
  // design normalises every one of these to its own peak. Callers that need a shared scale — the
  // load axis, whose whole point is one — pass `scaleMax` themselves.
  const max = scaleMax ?? (Math.max(...bars.map((b) => b.value), 0) || 1);
  return (
    <div
      className={`flex items-end ${className}`}
      style={
        fill
          ? { flex: "1 1 auto", minHeight: height, gap: `${gap}px` }
          : { height, gap: `${gap}px` }
      }
      role="presentation"
    >
      {bars.map((bar, index) => {
        // **Shares, not pixels.** A percentage resolves against whatever height the row ended up
        // with, so one expression serves both the fixed and the filled chart; computing pixels off
        // `height` would draw a filled chart at its floor and leave the rest of the panel empty.
        const share = percent(bar.value, max);
        const card = bar.hover;
        const mark = overlay?.values[index];
        const bounded =
          mark === null || mark === undefined
            ? null
            : clamp(
                ((mark - overlay!.domain[0]) / (overlay!.domain[1] - overlay!.domain[0])) * 100,
                0,
                100,
              );
        return (
          <span
            key={index}
            title={card ? undefined : bar.label}
            // The wrapper is full height so the overlay can be positioned against the whole scale
            // rather than against the bar, which is the point of an overlay.
            className={`relative flex h-full min-w-px flex-1 items-end ${card && onEnter ? "chart-mark" : ""}`}
            onMouseEnter={card && onEnter ? (event) => onEnter(event, card) : undefined}
            onMouseLeave={card && onEnter ? onLeave : undefined}
          >
            {bar.ghost && bar.ghost.value > 0 && (
              // Absolute so it is measured against the whole scale rather than stacked on the bar,
              // and `z-0` under the solid bar's `relative` so the two overlap in a defined order
              // whichever is taller. `border-b-0` because the axis line is the floor — a closed box
              // would draw a second rule along the bottom of every column.
              <span
                aria-hidden
                className="pointer-events-none absolute inset-x-0 bottom-0 z-0 rounded-t-[var(--r-2)] border border-b-0"
                style={{
                  height: `${percent(bar.ghost.value, max)}%`,
                  minHeight: minBarHeight,
                  borderColor: bar.ghost.color,
                }}
              />
            )}
            <span
              aria-hidden
              className="relative z-[1] w-full rounded-t-[var(--r-2)]"
              style={{
                height: bar.value > 0 ? `${share}%` : 0,
                // A non-zero value never renders as nothing, whatever the scale.
                minHeight: bar.value > 0 ? minBarHeight : 0,
                background: bar.color,
              }}
            />
            {bar.crest && bar.crest.value > 0 && (
              // `z-2` above the solid bar's `z-1` and the ghost's `z-0`: it is the topmost of the
              // three marks and must not be buried by whichever of them happens to be taller.
              //
              // `marginBottom: -1` straddles the level rather than sitting on top of it, so a bar
              // at the full scale keeps the rule inside the chart's `pt-[8px]` instead of pushing
              // 2px into the panel heading above.
              <span
                aria-hidden
                className="pointer-events-none absolute inset-x-0 z-[2] h-[2px]"
                style={{
                  bottom: `${percent(bar.crest.value, max)}%`,
                  marginBottom: -1,
                  background: bar.crest.color,
                }}
              />
            )}
            {bounded !== null && (
              <span
                aria-hidden
                className="pointer-events-none absolute inset-x-0 h-[2px]"
                style={{ bottom: `${bounded}%`, background: overlay!.color }}
              />
            )}
          </span>
        );
      })}
    </div>
  );
}
